<template>
  <section class="section-base form-section-base">
    <v-container>
      <div class="form-base">
        <v-row>
          <v-col cols="12">
            <div class="form-base__title">
              Сведения об обязательстве, в целях исполнения которого перечисляются в депозит нотариуса безналичные денежные средства
            </div>
          </v-col>
          <v-col cols="6">
            <CurrencyField
              v-model="$v.acceptanceDepositDetails.depositAmount.$model"
              label="Общий размер вносимых денежных средств в рублях"
              :readonly="depositAmountReadonly"
              :errorMessages="
                getValidationErrorText(
                  $v.acceptanceDepositDetails.depositAmount
                )
              "
            />
          </v-col>
        </v-row>

        <v-radio-group v-model="document" class="mt-0">
          <v-radio
            label="Сведения о документе, представляющем собой содержание сделки (наименование документа, дата и номер, сведения об удостоверении документа, если он удостоверен)"
            :value="true"
          />
          <template v-if="document">
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model.trim="$v.obligationDocument.name.$model"
                  :error-messages="
                    getValidationErrorText($v.obligationDocument.name)
                  "
                  label="Наименование"
                  outlined
                />
              </v-col>
              <v-col cols="6">
                <DateFieldCustom
                  @change="$v.obligationDocument.docDate.$model = $event"
                  :value="$v.obligationDocument.docDate.$model"
                  :error-message="$v.obligationDocument.docDate"
                  :max="new Date().toISOString()"
                  label="Дата"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model.trim="$v.obligationDocument.number.$model"
                  :error-messages="
                    getValidationErrorText($v.obligationDocument.number)
                  "
                  label="Номер"
                  outlined
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model.trim="$v.obligationDocument.attestation.$model"
                  :error-messages="
                    getValidationErrorText($v.obligationDocument.attestation)
                  "
                  label="Сведения об удостоверении документа, если он удостоверен"
                  outlined
                />
              </v-col>
            </v-row>
            <AttachDocumentSingle
              @change="
                $v.obligationDocument.file.$model = $event.file;
                $v.obligationDocument.fileSig.$model = $event.fileSig;
              "
              :value="{
                file: $v.obligationDocument.file.$model,
                fileSig: $v.obligationDocument.fileSig.$model,
              }"
              :error-messages="
                getValidationErrorText($v.obligationDocument.file)
              "
            />
          </template>

          <v-radio
            label="Иные основания для внесения безналичных денежных средств в депозит нотариуса, предусмотренные законодательством Российской Федерации"
            :value="false"
          />
          <v-row v-if="!document">
            <v-col cols="12">
              <v-select
                label="Список"
                :items="OBLIGATION_TYPES"
                outlined
                v-model="obligationType"
              />
              <v-text-field
                v-model="
                  $v.acceptanceDepositDetails.informationAboutObligation.$model
                "
                :error-messages="
                  getValidationErrorText(
                    $v.acceptanceDepositDetails.informationAboutObligation
                  )
                "
                label="Сведения об основании для внесения в депозит"
                outlined
              />
            </v-col>
          </v-row>
        </v-radio-group>
      </div>

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import { and, maxLength, required, requiredIf } from 'vuelidate/lib/validators';
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import {
  DATE_FORMAT,
  DATE_FUTURE,
  DATE_PAST,
  getValidationErrorText,
  EMOJI,
  CURRENCY_MAX_LENGTH,
  CURRENCY_ONLY_POSITIVE,
} from '@/utils/validate';
import Footer from '@/components/services/elements/Footer';
import DateFieldCustom from '@/components/base/DateFieldCustom';
import AttachDocumentSingle from '@/components/base/AttachDocument/AttachDocumentSingle';
import CurrencyField from '@/components/base/CurrencyField.vue';
import { checkInvalid, leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import { StepDirection, StepName } from '@/types/services';
import {AcceptanceDepositStep, OBLIGATION_TYPES} from '@/constants';

export default {
  name: 'DepositDetails',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Footer,
    DateFieldCustom,
    AttachDocumentSingle,
    CurrencyField,
  },
  data: () => ({
    OBLIGATION_TYPES,
    StepDirection,
    StepName,
    leaveStep,
    getValidationErrorText,
    invalidFields: [],
    depositAmountReadonly: true,
    document: true,
    acceptanceDepositDetails: {
      depositAmount: null,
      depositCurrency: 'RUB',
      informationAboutObligation: '',
    },
    obligationType: '',
    obligationDocument: {
      name: '',
      docDate: '',
      number: '',
      attestation: '',
      file: null,
      fileSig: null,
    },
  }),
  validations() {
    const stepValidation = {
      acceptanceDepositDetails: {
        depositAmount: {
          required: requiredIf(() => !this.depositAmountReadonly),
          CURRENCY_MAX_LENGTH,
          CURRENCY_ONLY_POSITIVE,
        },
      },
    };

    if (this.document) {
      stepValidation.obligationDocument = {
        name: {
          required,
          maxLength: maxLength(1000),
          EMOJI,
        },
        docDate: {
          required,
          DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
        },
        number: {
          required,
          maxLength: maxLength(50),
          EMOJI,
        },
        attestation: {
          maxLength: maxLength(4000),
          EMOJI,
        },
        file: {},
        fileSig: {},
      };
    } else {
      stepValidation.acceptanceDepositDetails = deepmerge(
        stepValidation.acceptanceDepositDetails,
        {
          informationAboutObligation: {
            required,
            maxLength: maxLength(4000),
            EMOJI,
          },
        }
      );
    }

    return stepValidation;
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      const ADD = cloneDeep(ServicesModule.statement.acceptanceDepositDetails);

      if (ADD) {
        delete ADD.depositAmount; // vue-currency-input init later
        this.acceptanceDepositDetails = deepmerge(
          this.acceptanceDepositDetails,
          ADD
        );
      }

      if (ServicesModule.statement.obligationDocument) {
        this.obligationDocument = cloneDeep(
          ServicesModule.statement.obligationDocument
        );
      }

      this.document = !this.acceptanceDepositDetails.informationAboutObligation;

      // предзаполнения валюты
      const creditors = ServicesModule.statement.subjects.creditors;
      const depositAmount =
        ServicesModule.statement.acceptanceDepositDetails?.depositAmount;

      this.depositAmountReadonly = Boolean(creditors.length);
      this.acceptanceDepositDetails.depositAmount = depositAmount || null;

      if (ServicesModule.statement.acceptanceDepositDetails.isEasement) this.obligationType = 'isEasement'
    },

    assemblyModel() {
      const model = {
        acceptanceDepositDetails: cloneDeep(this.acceptanceDepositDetails),
        obligationDocument: cloneDeep(this.obligationDocument),
      };

      if (this.document) {
        model.acceptanceDepositDetails.informationAboutObligation = null;
        this.obligationType = '';
        model.acceptanceDepositDetails.isEasement = false;
      } else {
        model.obligationDocument = null;
        model.acceptanceDepositDetails.isEasement = this.obligationType === 'isEasement';
      }

      return model;
    },

    touchFields() {
      this.invalidFields = [];
      this.$v.$touch();
      checkInvalid(this.$v, this.invalidFields);
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: AcceptanceDepositStep.DEPOSIT_DETAILS,
      });
    },
  },
};
</script>
